import type { bookings } from '@repletoryreach/sdk-api';
import { NumberInput, Select, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';

export type Answer = string | number | boolean;

/** One of a service's questions, as the input its type calls for. */
export function QuestionField({ question: q, value, onChange, error }: {
  question: bookings.Question;
  value: Answer | undefined;
  onChange: (v: Answer | undefined) => void;
  error?: string;
}) {
  const t = useT();
  const common = { label: q.label, required: q.required, error };
  switch (q.type) {
    case 'number':
      return (
        <NumberInput
          {...common}
          value={typeof value === 'number' ? value : ''}
          onChange={(v) => onChange(v === '' ? undefined : v)}
        />
      );
    case 'date':
      return <TextInput {...common} type="date" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.currentTarget.value || undefined)} />;
    case 'select':
      return (
        <Select
          {...common}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.currentTarget.value || undefined)}
          data={[{ value: '', label: t('common.select') }, ...(q.options ?? []).map((o) => ({ value: o, label: o }))]}
        />
      );
    case 'yes_no':
      return (
        <Select
          {...common}
          value={value === true ? 'yes' : value === false ? 'no' : ''}
          onChange={(e) => onChange(e.currentTarget.value === '' ? undefined : e.currentTarget.value === 'yes')}
          data={[
            { value: '', label: t('common.select') },
            { value: 'yes', label: t('common.yes') },
            { value: 'no', label: t('common.no') }
          ]}
        />
      );
    case 'address':
      return <Textarea {...common} minRows={2} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.currentTarget.value || undefined)} />;
    default:
      return <TextInput {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.currentTarget.value || undefined)} />;
  }
}

/** The keys of required questions left unanswered. */
export function missingAnswers(questions: readonly bookings.Question[], answers: Record<string, Answer | undefined>): string[] {
  return questions.filter((q) => q.required && (answers[q.key] === undefined || answers[q.key] === '')).map((q) => q.key);
}
